/**
 * Main React application component
 */

import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import AffinityPage from './components/AffinityPage';
import LandingPage from './components/cdn/LandingPage';
import type { FeedbackData } from './components/FeedbackForm';
import LoadingScreen from './components/LoadingScreen';
import ModerationPage from './components/ModerationPage';
import OnboardingFlow from './components/OnboardingFlow';
import OnboardingPreview from './components/OnboardingPreview';
import PersonalizedPage from './components/PersonalizedPage';
import UserTestingPage from './components/UserTestingPage';
import { isCdnMode } from './config/siteMode';
import { useCustomerHistory, useRecommendationsByIdentifier } from './hooks/useRecommendations';
import {
  type FeedbackRequest,
  getAuthMe,
  getOnboarding,
  isQueuedResponse,
  type OnboardingQuestion,
  type RecommendationByEmailResponse,
  type RecommendationResult,
  submitFeedback,
} from './services/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Pull a human-readable message out of an Axios-style error payload, or null
// when none is present. Backend messages are English-only.
function extractBackendErrorMessage(error: any): string | null {
  const responseData = error?.response?.data;
  if (!responseData) {
    return null;
  }

  const detail = responseData.detail;
  if (typeof detail === 'object' && detail?.message) {
    return detail.message;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  return responseData.message ?? null;
}

function AppContent({
  autoEmail,
  cdnMode = false,
  customerFirstName,
  enableOnboarding = false,
}: Readonly<{
  autoEmail?: string;
  cdnMode?: boolean;
  customerFirstName?: string;
  /** Route cold-start (not-in-Neo4j) shoppers through the onboarding flow first. */
  enableOnboarding?: boolean;
}>) {
  const { t } = useTranslation();
  const [testingResult, setTestingResult] = useState<RecommendationResult | null>(null);
  const [testingError, setTestingError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [testingEmail, setTestingEmail] = useState(autoEmail || '');
  const [testingSubmittedEmail, setTestingSubmittedEmail] = useState('');
  const [shopifyCustomerId, setShopifyCustomerId] = useState('');
  const [onboardingPhase, setOnboardingPhase] = useState<'idle' | 'checking' | 'questions'>('idle');
  const [onboardingQuestions, setOnboardingQuestions] = useState<OnboardingQuestion[]>([]);

  const recommendationMutation = useRecommendationsByIdentifier();
  // History feeds the profile, history and feedback-form tabs, all of which are
  // internal-only. Customers have nowhere to see it, so it is not worth asking
  // them to download it.
  const customerHistoryQuery = useCustomerHistory(shopifyCustomerId, { enabled: !cdnMode });
  const hasAutoSubmitted = useRef(false);

  const applyRecommendationResponse = (response: RecommendationByEmailResponse) => {
    if (isQueuedResponse(response)) {
      setShopifyCustomerId(response.shopify_customer_id || '');
      // Backend messages are English-only; CDN mode shows the localized copy.
      setInfoMessage(
        cdnMode ? t('status.queuedMessage') : response.message || t('status.queuedMessage')
      );
      return;
    }

    if ((response as any).status === 'not_personalized') {
      setShopifyCustomerId((response as any).shopify_customer_id || '');
      setInfoMessage(t('status.notPersonalized'));
      return;
    }

    const result = response;
    setTestingResult(result);
    if (result.shopify_customer_id) {
      setShopifyCustomerId(result.shopify_customer_id);
    }
  };

  const handleTestingIdentifierSubmit = async (identifier: string) => {
    try {
      setTestingError(null);
      setInfoMessage(null);
      setTestingResult(null);
      setShopifyCustomerId('');
      setFeedbackSuccess(false);
      setFeedbackError(null);
      const response = await recommendationMutation.mutateAsync(identifier);
      applyRecommendationResponse(response);
    } catch (error: any) {
      // Backend error details are English-only; suppress them in CDN mode.
      const backendMessage = cdnMode ? null : extractBackendErrorMessage(error);
      setTestingError(backendMessage || t('status.defaultError'));
    }
  };

  // Cold-start router: check onboarding state before the warm recommendations
  // path. Cold shoppers (not in Neo4j) get the question flow; already-onboarded
  // shoppers get their blended carousels; warm shoppers fall through unchanged.
  const bootstrapWithOnboarding = async (id: string) => {
    setOnboardingPhase('checking');
    try {
      const state = await getOnboarding(id);
      setShopifyCustomerId(state.shopify_customer_id || id);

      if (state.needs_onboarding && state.questions && state.questions.length > 0) {
        setOnboardingQuestions(state.questions);
        setOnboardingPhase('questions');
        return;
      }

      if (
        state.recommendations_by_profile &&
        Object.keys(state.recommendations_by_profile).length > 0
      ) {
        applyRecommendationResponse(state as RecommendationResult);
        setOnboardingPhase('idle');
        return;
      }

      setOnboardingPhase('idle');
      await handleTestingIdentifierSubmit(id);
    } catch {
      setOnboardingPhase('idle');
      await handleTestingIdentifierSubmit(id);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: run-once auto-submit on mount, guarded by ref
  useEffect(() => {
    if (autoEmail && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      setTestingSubmittedEmail(autoEmail);
      if (enableOnboarding) {
        bootstrapWithOnboarding(autoEmail);
      } else {
        handleTestingIdentifierSubmit(autoEmail);
      }
    }
  }, [autoEmail]);

  const handleFeedbackSubmit = async (feedback: FeedbackData) => {
    try {
      setFeedbackSubmitting(true);
      setFeedbackError(null);

      const feedbackRequest: FeedbackRequest = {
        customer_id: feedback.customer_id,
        email: testingSubmittedEmail || undefined,
        overall_rating: feedback.overall_rating,
        comments: feedback.comments,
        book_ratings: feedback.book_ratings.map((br) => ({
          isbn: br.isbn,
          title: br.title,
          rating: br.rating as 'thumbs_up' | 'thumbs_down',
        })),
        history_book_ratings: feedback.history_book_ratings.map((br) => ({
          isbn: br.isbn,
          title: br.title,
          rating: br.rating,
          is_gift: br.is_gift,
        })),
        profile_ratings: feedback.profile_ratings.map((pr) => ({
          name: pr.name,
          rating: pr.rating as 'thumbs_up' | 'thumbs_down',
        })),
        timestamp: feedback.timestamp,
      };

      const response = await submitFeedback(feedbackRequest);

      if (response.success) {
        setFeedbackSuccess(true);
      } else {
        setFeedbackError(response.error || 'Failed to submit feedback');
      }
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      setFeedbackError(error?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  if (onboardingPhase === 'checking' && !testingResult) {
    const loadingMessages = t('onboarding.loading', { returnObjects: true }) as string[];
    return <LoadingScreen messages={loadingMessages} />;
  }

  if (onboardingPhase === 'questions' && !testingResult) {
    return (
      <OnboardingFlow
        shopifyCustomerId={shopifyCustomerId}
        questions={onboardingQuestions}
        cdnMode={cdnMode}
        customerFirstName={customerFirstName}
        onComplete={(result) => {
          setOnboardingPhase('idle');
          applyRecommendationResponse(result);
        }}
        onSkip={() => {
          setOnboardingPhase('idle');
          setInfoMessage(t('status.notPersonalized'));
        }}
      />
    );
  }

  return (
    <UserTestingPage
      onEmailSubmit={handleTestingIdentifierSubmit}
      onFeedbackSubmit={handleFeedbackSubmit}
      result={testingResult}
      isLoading={recommendationMutation.isPending}
      error={testingError}
      infoMessage={infoMessage}
      feedbackSubmitting={feedbackSubmitting}
      feedbackError={feedbackError}
      feedbackSuccess={feedbackSuccess}
      email={testingEmail}
      setEmail={setTestingEmail}
      setSubmittedEmail={setTestingSubmittedEmail}
      shopifyCustomerId={shopifyCustomerId}
      customerHistory={customerHistoryQuery.data?.history ?? []}
      customerHistoryLoading={customerHistoryQuery.isLoading}
      cdnMode={cdnMode}
      customerFirstName={customerFirstName}
    />
  );
}

/**
 * Customer-facing entry (CDN mode): gates the experience on the Shopify
 * session. Unauthenticated visitors see the landing page; authenticated
 * customers get their recommendations loaded automatically from the session.
 */
function CdnHome() {
  const { t } = useTranslation();

  // Shopify callback redirects to `/?signed_in=1`. Strip the marker so a refresh
  // does not leave a stale query param; the min-duration welcome loader lives on
  // onboarding submit (OnboardingFlow), not on plain sign-in.
  useEffect(() => {
    const url = new URL(globalThis.location.href);
    if (!url.searchParams.has('signed_in')) return;
    url.searchParams.delete('signed_in');
    globalThis.history.replaceState({}, '', url.toString());
  }, []);

  const authQuery = useQuery({
    queryKey: ['authMe'],
    queryFn: getAuthMe,
    staleTime: 60_000,
  });

  // Natural loader while the session cookie is checked — no artificial delay.
  if (authQuery.isLoading) {
    const loadingMessages = t('onboarding.loading', { returnObjects: true }) as string[];
    return <LoadingScreen messages={loadingMessages} />;
  }

  const session = authQuery.data;
  if (!session?.authenticated || !session.shopify_customer_id) {
    return <LandingPage />;
  }

  return (
    <AppContent
      autoEmail={session.shopify_customer_id}
      cdnMode
      enableOnboarding
      customerFirstName={session.first_name}
    />
  );
}

function App() {
  if (isCdnMode()) {
    // Customer-facing experience only: internal tools (moderation, demo
    // shortcuts, ID-based deep links) are not exposed on the CDN domains.
    return (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<CdnHome />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route path="/heather-kids" element={<AppContent autoEmail="heather-kids" cdnMode />} />
          <Route path="/heather" element={<AppContent autoEmail="heather" cdnMode />} />
          <Route path="/recommendations/:shopifyCustomerId" element={<PersonalizedPage />} />
          <Route path="/moderation" element={<ModerationPage />} />
          <Route path="/reranker" element={<AffinityPage />} />
          <Route path="/onboarding-preview" element={<OnboardingPreview />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
